import {
  ButtonTertiary,
  cssColorValue,
  desktopMesto,
  IconArrowTopRight,
  PopoverNavigation,
  PopoverNavigationItem,
  PopoverTrigger,
  semanticColors,
  spacer20,
  spacer24,
  spacer4,
  spacer8,
} from '@spotify-internal/encore-web';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { PopoverLink } from 'components/Masthead/Header/types';

const PopoverNav = styled(PopoverNavigation)`
  padding: ${spacer8};

  [dir='rtl'] & * {
    direction: rtl;
    & > svg {
      transform: rotateY(180deg);
    }
  }
`;

const PopoverNavLinkText = styled.span<{
  openInNewWindow?: boolean;
  iconLeading: boolean;
  level: number;
}>`
  && {
    margin-inline-start: ${({ level, iconLeading }) => {
      if (!iconLeading && level === 2) {
        return spacer24;
      }

      if (iconLeading) {
        return spacer8;
      }

      return 0;
    }};
    margin-inline-end: ${({ openInNewWindow }) => (openInNewWindow ? 'auto' : 0)};
  }
`;

const PopoverNavLink = styled(ButtonTertiary)<{
  level: number;
}>`
  cursor: pointer;
  color: ${cssColorValue(semanticColors.textBase)};
  justify-content: flex-start;
  min-block-size: auto;
  padding: 0;
  width: 100%;
  ${desktopMesto};

  &:active,
  &:hover,
  &:focus {
    transform: none;
  }

  &:active {
    color: ${cssColorValue(semanticColors.textBase)};
    opacity: 1;
  }

  & > * {
    margin: 0;
  }

  &:focus {
    & ${PopoverNavLinkText} + span > svg {
      display: block;
    }
  }

  &::after {
    bottom: 0;
    inline-size: ${({ level, iconLeading }) => {
      if (!iconLeading && level === 2) {
        return `calc(100% - ${spacer24})`;
      }

      return '100%';
    }};
    margin-inline-start: ${({ level, iconLeading }) => {
      if (!iconLeading && level === 2) {
        return spacer24;
      }

      return 0;
    }};
  }
`;

const PopoverNavItem = styled(PopoverNavigationItem)`
  border-radius: ${spacer4};
  cursor: pointer;
  padding-block: calc(${spacer20} / 2);
  padding-inline: ${spacer8};

  & ${PopoverNavLinkText} + span > svg {
    display: none;
  }

  &:hover {
    background-color: ${cssColorValue(semanticColors.backgroundElevatedHighlight)};

    & ${PopoverNavLinkText} {
      color: ${cssColorValue(semanticColors.textBrightAccent)};
    }

    & ${PopoverNavLinkText} + span > svg {
      display: block;
    }
  }
`;

interface Props {
  children: ReactNode;
  isOpen: boolean;
  links: PopoverLink[];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export const NavBarPrimaryLinksPopover = ({ children, isOpen, links, onMouseEnter, onMouseLeave }: Props) => {
  return (
    <PopoverTrigger
      onShow={() => {}}
      onHide={() => {}}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      placement={PopoverTrigger.bottom}
      overlay={
        isOpen && (
          <PopoverNav arrow={PopoverNavigation.top}>
            {links.map(({ target, href, level, linkText, iconLeading, ...rest }, i) => (
              <PopoverNavItem key={i}>
                <PopoverNavLink
                  target={target}
                  href={href}
                  iconLeading={iconLeading}
                  level={level}
                  iconTrailing={target === '_blank' ? () => <IconArrowTopRight size="small" /> : undefined}
                  {...rest}
                >
                  <PopoverNavLinkText
                    openInNewWindow={target === '_blank'}
                    iconLeading={Boolean(iconLeading)}
                    level={level}
                  >
                    {linkText}
                  </PopoverNavLinkText>
                </PopoverNavLink>
              </PopoverNavItem>
            ))}
          </PopoverNav>
        )
      }
    >
      {children}
    </PopoverTrigger>
  );
};
